// Imports
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { useAppDispatch } from '../../../../shareables/foundation/front-end/redux/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '../../../../shareables/foundation/admin-ui/components/content/box';
import Heading from '../../../../shareables/foundation/admin-ui/components/content/heading';
import Radio from '../../../../shareables/foundation/front-end/components/forms/fields/radio';
import APIForm, { APIFormOptions } from '../../../../shareables/integrations/api/components/form';
import apiActions from '../../../../shareables/integrations/api/redux/actions';
import { GetSubscriptionResponse } from '../../../../shareables/types/api/main/reseller/website/subscription/get';
import determineEmailAccountLimit from '../../../../shareables/utils/billing/determine-email-account-limit';
import specialSizes from '../../../../shareables/utils/billing/special-sizes';
import subscriptionAmountMapping from '../../../../shareables/utils/billing/subscription-amount-mapping';
import formatCurrency from '../../../../shareables/utils/project-specific/formatting/format-currency';
import { PutSubscriptionParameters } from '../../../../shareables/types/api/main/reseller/website/subscription/put';
import ResellerContext from '../../../resources/reseller-provider/context';
import planMapping from '../../../../shareables/utils/billing/plan-mapping';
import planNameMapping from '../../../../shareables/utils/billing/plan-name-mapping';
import styled from 'styled-components/macro';
import determineMonthlySubscriptionAmount from '../../../../shareables/utils/billing/determine-monthly-subscription-amount';
import availableSubscriptionSizes from '../../../../shareables/utils/billing/available-subscription-sizes';
import Select from '../../../../shareables/foundation/front-end/components/forms/fields/select';
import PlanSelector from '../../../../shareables/front-end/subscription-plans/plan-selector';


// Styled components
const PlansWrapper = styled.div`
	margin-bottom: 1.25em;
`;

const ExtendedSize = styled.div`
	div {
		display: inline-block;
		margin-bottom: 0 !important;
	}
	
	select {
		height: 1.5625rem;
	}
	
	select + span {
		top: -0.4063rem;
	}
`;

const Accounts = styled.span`
	white-space: nowrap;
`;


// Define params
type Params = {
	websiteID: string;
};


// Define the accepted props
interface Props {
	storybookSubscription?: GetSubscriptionResponse;
	storybookTakeEffect?: 'NOW' | 'RENEWAL';
	storybookDisplayTaxVerbiage?: true;
}


// Function component
const ChangeSubscription: React.FC<Props> = ({
	storybookSubscription,
	storybookTakeEffect,
	storybookDisplayTaxVerbiage,
}) => {
	// Use context
	const reseller = useContext(ResellerContext);
	
	
	// Use state
	const [subscription, setSubscription] = useState<GetSubscriptionResponse | undefined>(storybookSubscription);
	const [selectedSize, setSelectedSize] = useState<number | undefined>(storybookSubscription?.size ?? undefined);
	const [takeEffect, setTakeEffect] = useState<'NOW' | 'RENEWAL' | undefined>(storybookTakeEffect);
	const [selectedPlan, setSelectedPlan] = useState(subscription?.plan);
	const [dropdownSize, setDropdownSize] = useState<number>();
	const [selectedPlanChanged, setSelectedPlanChanged] = useState(false);
	const [planErrorMessage, setPlanErrorMessage] = useState('');
	
	
	// Use React Router functionality
	const navigate = useNavigate();
	const { websiteID } = useParams<Params>();
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Load size on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetSubscriptionResponse>({
				action: 'GET',
				uri: `/reseller/${reseller?.id ?? ''}/website/${websiteID ?? ''}/subscription`,
				completion: (json) => {
					setSubscription(json.response);
					setSelectedSize(json.response.size);
					setSelectedPlan(json.response.plan);
				},
			})
		);
	}, [dispatch, reseller?.id, websiteID]);
	
	
	// Use ref
	const takeEffectSetter = useRef<(value: string) => void>();
	const selectListSizeGetter = useRef<() => string | number>();
	const radioSizeSetter = useRef<(value: string) => void>();
	const firstExtendedSizeRef = useRef<number>();
	
	
	// Initialize
	const increasingSize = Boolean(
		selectedSize && subscription && selectedSize > subscription.eligibilityFactors.effectiveDetails.size
	);
	
	const increasingPlan = Boolean(
		selectedPlan && subscription && selectedPlan > subscription.eligibilityFactors.effectiveDetails.plan
	);
	
	const decreasingSize = Boolean(
		selectedSize && subscription && selectedSize < subscription.eligibilityFactors.effectiveDetails.size
	);
	
	const decreasingPlan = Boolean(
		selectedPlan && subscription && selectedPlan < subscription.eligibilityFactors.effectiveDetails.plan
	);
	
	
	// Determine whether or not "Take effect: Now" is allowed
	const allowNow = !decreasingSize && !decreasingPlan && (increasingSize || increasingPlan);
	
	
	// Detect if pending first payment
	const pendingFirst =
		subscription?.eligibilityFactors.effectiveDetails.plan === planMapping.free ||
		subscription?.eligibilityFactors.effectiveDetails.size === specialSizes.TRIAL;
	
	
	// Clear "take effect" value if needed
	useEffect(() => {
		if (!allowNow && !pendingFirst && takeEffect === 'NOW') {
			takeEffectSetter.current?.('');
		}
	}, [allowNow, pendingFirst, takeEffect]);
	
	
	// Build size choices
	const sizeChoices = useMemo(() => {
		// Type guard
		const isValidPlan = (plan: number): plan is (typeof planMapping)[keyof typeof planMapping] => {
			return Object.keys(planNameMapping).map(Number).includes(plan);
		};
		
		
		// Return early
		if (selectedPlan === undefined || !isValidPlan(selectedPlan)) {
			return [];
		}
		
		
		// Decide whether tax verbiage should be displayed
		const displaySalesTaxVerbiage = reseller?.regionRequiresSalesTax || storybookDisplayTaxVerbiage;
		
		
		// Create base size choices
		const baseSizeChoices = Object.keys(subscriptionAmountMapping[selectedPlan]).map((sizeChoice) => {
			// Initialize
			const typedSizeChoice = parseInt(sizeChoice);
			const emailAccountLimit = determineEmailAccountLimit({ plan: selectedPlan, size: typedSizeChoice });
			
			
			// Handle ineligible because of size usage
			if ((subscription?.eligibilityFactors.currentUsage.size ?? 0) > typedSizeChoice) {
				return {
					label: (
						<React.Fragment>
							{typedSizeChoice.toLocaleString()} accounts
							<small className='is-gray'>
								Currently have too many{' '}
								<Accounts>accounts ({subscription?.eligibilityFactors.currentUsage.size.toLocaleString()})</Accounts>
							</small>
						</React.Fragment>
					),
					value: sizeChoice,
					disabled: true,
				};
			}
			
			
			// Handle ineligible because of email account usage
			if ((subscription?.eligibilityFactors.currentUsage.emailAccounts ?? 0) > emailAccountLimit) {
				return {
					label: (
						<React.Fragment>
							{typedSizeChoice.toLocaleString()} accounts
							<small className='is-gray'>
								Currently have too many email{' '}
								<Accounts>
									accounts ({subscription?.eligibilityFactors.currentUsage.emailAccounts.toLocaleString()})
								</Accounts>
							</small>
						</React.Fragment>
					),
					value: sizeChoice,
					disabled: true,
				};
			}
			
			
			// Handle eligible choices
			return {
				label: (
					<React.Fragment>
						{Number(sizeChoice).toLocaleString()} accounts
						<small className='is-gray'>
							{formatCurrency(
								determineMonthlySubscriptionAmount({
									plan: selectedPlan,
									size: Number(sizeChoice),
									yearly: false,
								}).value * 0.75
							)}
							/mo{displaySalesTaxVerbiage ? ' + tax' : ''}
						</small>
					</React.Fragment>
				),
				value: sizeChoice,
			};
		});
		
		
		// Return Deluxe plan size choices
		if (selectedPlan === planMapping.deluxe || selectedPlan === planMapping.grandfatheredDeluxe) {
			return baseSizeChoices;
		}
		
		
		// Return Inform plan size choices
		if (selectedPlan === planMapping.inform) {
			return [
				...baseSizeChoices,
				{
					label: (
						<span className='is-gray'>
							<em>
								{Number(baseSizeChoices[baseSizeChoices.length - 1].value) + 1}+ accounts requires the Engage plan
							</em>
						</span>
					),
					value: 'disabled',
					disabled: true,
				},
			];
		}
		
		
		// Create extended size choices
		const allSizes = availableSubscriptionSizes(selectedPlan);
		
		const baseSizeSet = new Set(baseSizeChoices.map((choice) => Number(choice.value)));
		
		const extendedPlanSizes = allSizes.filter((planSize) => {
			// Initialize
			const emailAccountLimit = determineEmailAccountLimit({ plan: selectedPlan, size: planSize });
			
			
			// Only include eligible sizes
			return (
				!baseSizeSet.has(planSize) &&
				(subscription?.eligibilityFactors.currentUsage.size ?? 0) < planSize &&
				(subscription?.eligibilityFactors.currentUsage.emailAccounts ?? 0) < emailAccountLimit
			);
		});
		
		const extendedSizeChoices = (
			<ExtendedSize>
				<Select
					name='extendedSizeChoice'
					exclude
					bindGetter={(getter) => (selectListSizeGetter.current = getter)}
					onChange={(newValue) => {
						setSelectedSize(Number(newValue));
						setDropdownSize(Number(newValue));
					}}
					onClick={() => radioSizeSetter.current?.('selectList')}
					initialValue={extendedPlanSizes.find((extendedPlanSize) => extendedPlanSize === subscription?.size)}
				>
					{extendedPlanSizes.map((size) => {
						return (
							<option key={size} value={size}>
								{size.toLocaleString()}
							</option>
						);
					})}
				</Select>{' '}
				accounts
				<small className='is-gray'>
					{formatCurrency(
						determineMonthlySubscriptionAmount({
							plan: selectedPlan,
							size:
								dropdownSize ??
								extendedPlanSizes.find((extendedPlanSize) => extendedPlanSize === subscription?.size) ??
								extendedPlanSizes[0],
							yearly: false,
						}).value * 0.75
					)}
					/mo{displaySalesTaxVerbiage ? ' + tax' : ''}
				</small>
			</ExtendedSize>
		);
		
		
		// Update ref
		firstExtendedSizeRef.current = extendedPlanSizes[0];
		
		
		// Return Engage plan size choices
		return [
			...baseSizeChoices,
			{
				label: extendedSizeChoices,
				value: 'selectList',
			},
		];
	}, [selectedPlan, dropdownSize, subscription, reseller?.regionRequiresSalesTax, storybookDisplayTaxVerbiage]);
	
	
	// If loading, then display nothing
	if (!subscription || !selectedSize || !reseller || !selectedPlan) {
		return null;
	}
	
	
	// Build form options
	const formOptions: APIFormOptions<PutSubscriptionParameters, undefined, undefined> = {
		action: 'PUT',
		uri: `/reseller/${reseller?.id ?? ''}/website/${websiteID ?? ''}/subscription`,
		submitButton: {
			text: 'Change',
		},
		extraParameters: {
			size: selectedSize,
		},
		recoverState: false,
		errorHandlers: [
			{
				match: {
					code: 'PARAMETER_ERROR',
					parameter: 'plan',
				},
				handler: (json) => {
					if (json.message.toLowerCase().includes('this is required')) {
						setPlanErrorMessage('Please select a plan above.');
						return;
					}
					
					setPlanErrorMessage(json.message);
				},
			},
		],
	};
	
	
	// Return JSX
	return (
		<React.Fragment>
			<Heading size={1}>Change subscription</Heading>
			
			<APIForm options={formOptions} onCancel={() => navigate(-1)} onCompletion={() => navigate('..')}>
				<Box>
					<PlansWrapper
						hidden={subscription.plan === planMapping.deluxe || subscription.plan === planMapping.grandfatheredDeluxe}
					>
						<PlanSelector
							fieldLabel='Plan'
							currentUsage={subscription.eligibilityFactors.currentUsage}
							handleSelectedPlanChange={(newValue) => {
								setSelectedPlan(Number(newValue));
								setSelectedPlanChanged(true);
								setPlanErrorMessage('');
							}}
							selectedPlan={selectedPlan}
							selectedPlanChanged={selectedPlanChanged}
							errorMessage={planErrorMessage}
							subscriptionPlan={subscription.plan}
							effectivePlan={subscription.eligibilityFactors.effectiveDetails.plan}
						/>
					</PlansWrapper>
					
					<Radio
						name='size'
						label='Size'
						note={
							<React.Fragment>
								{/* eslint-disable-next-line react/jsx-no-target-blank */}
								<a
									href='https://help.hoa-express.com/en/articles/6698499-selecting-a-subscription-size'
									target='_blank'
								>
									Learn more
								</a>{' '}
								about selecting the right size
							</React.Fragment>
						}
						choices={sizeChoices}
						required
						exclude
						initialValue={
							sizeChoices.map((choice) => choice.value).includes(subscription.size.toString())
								? subscription.size.toString()
								: 'selectList'
						}
						onChange={(newValue) => {
							if (newValue === 'selectList') {
								setSelectedSize(Number(dropdownSize ?? firstExtendedSizeRef.current));
								return;
							}
							
							setSelectedSize(parseInt(newValue));
						}}
						bindSetter={(setter) => (radioSizeSetter.current = setter)}
					/>
					
					<Radio
						name='takeEffect'
						label='Take effect'
						choices={
							pendingFirst
								? [
										{
											label: (
												<React.Fragment>
													Now
													<small className='is-gray'>required if pending first payment</small>
												</React.Fragment>
											),
											value: 'RENEWAL', // When pending first, renewal date is never in the future, so "at renewal" is effectively now
											disabled: true,
										},
										{
											label: 'At the next renewal',
											value: 'IGNORE',
											disabled: true,
										},
									]
								: [
										{
											label: (
												<React.Fragment>
													Now
													<small className='is-gray'>
														{allowNow ? 'via prorated payment' : 'Only available if upgrading'}
													</small>
												</React.Fragment>
											),
											value: 'NOW',
											disabled: !allowNow,
										},
										{
											label: 'At the next renewal',
											value: 'RENEWAL',
										},
									]
						}
						required
						initialValue={pendingFirst ? 'RENEWAL' : takeEffect}
						bindSetter={(setter) => (takeEffectSetter.current = setter)}
						onChange={(newValue) => setTakeEffect(newValue as 'NOW' | 'RENEWAL')}
					/>
				</Box>
				
				<AnimateHeight
					duration={400}
					height={takeEffect === 'NOW' ? 'auto' : 0}
					animateOpacity
					style={{ marginBottom: takeEffect === 'NOW' ? '1.25em' : 0 }}
				>
					<Box>
						<p>You will be immediately charged a prorated amount for the current billing period.</p>
					</Box>
				</AnimateHeight>
			</APIForm>
		</React.Fragment>
	);
};

export default ChangeSubscription;
